import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import './dashboard.css'

// Plain-SVG charts in the Saflens dashboard style. Colours come from the
// dashboard CSS tokens (orange / black / neutral only). Every chart has a
// hover layer and degrades to an empty state handled by the caller.

// ─── Utilities ───────────────────────────────────────────────────────

export function useMeasure<T extends HTMLElement>(): [React.RefObject<T>, number] {
  const ref = useRef<T>(null)
  const [w, setW] = useState(0)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    setW(el.getBoundingClientRect().width)
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(entries => setW(entries[0].contentRect.width))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return [ref, w]
}

/** "Nice" axis maximum and ticks for a value range starting at 0. */
export function niceScale(max: number, ticks = 4): { max: number; step: number; ticks: number[] } {
  if (!Number.isFinite(max) || max <= 0) return { max: ticks, step: 1, ticks: Array.from({ length: ticks + 1 }, (_, i) => i) }
  const raw = max / ticks
  const mag = Math.pow(10, Math.floor(Math.log10(raw)))
  const norm = raw / mag
  const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * mag
  const niceMax = step * Math.ceil(max / step)
  const out: number[] = []
  for (let v = 0; v <= niceMax + step / 2; v += step) out.push(Math.round(v * 1e6) / 1e6)
  return { max: niceMax, step, ticks: out }
}

/** Monotone cubic interpolation path (no overshoot below zero). */
export function smoothPath(pts: Array<[number, number]>): string {
  const n = pts.length
  if (n === 0) return ''
  if (n === 1) return `M${pts[0][0]},${pts[0][1]}`
  const dx: number[] = [], dy: number[] = [], m: number[] = []
  for (let i = 0; i < n - 1; i++) { dx.push(pts[i + 1][0] - pts[i][0]); dy.push(pts[i + 1][1] - pts[i][1]); m.push(dy[i] / (dx[i] || 1)) }
  const t: number[] = [m[0]]
  for (let i = 1; i < n - 1; i++) t.push(m[i - 1] * m[i] <= 0 ? 0 : (m[i - 1] + m[i]) / 2)
  t.push(m[n - 2])
  for (let i = 0; i < n - 1; i++) {
    if (m[i] === 0) { t[i] = 0; t[i + 1] = 0; continue }
    const a = t[i] / m[i], b = t[i + 1] / m[i], s = a * a + b * b
    if (s > 9) { const k = 3 / Math.sqrt(s); t[i] = k * a * m[i]; t[i + 1] = k * b * m[i] }
  }
  let d = `M${pts[0][0]},${pts[0][1]}`
  for (let i = 0; i < n - 1; i++) {
    const c1x = pts[i][0] + dx[i] / 3, c1y = pts[i][1] + (t[i] * dx[i]) / 3
    const c2x = pts[i + 1][0] - dx[i] / 3, c2y = pts[i + 1][1] - (t[i + 1] * dx[i]) / 3
    d += ` C${c1x},${c1y} ${c2x},${c2y} ${pts[i + 1][0]},${pts[i + 1][1]}`
  }
  return d
}

const fmtDefault = (v: number) => (Math.abs(v) >= 1000 ? `${Math.round(v / 100) / 10}k` : `${Math.round(v * 10) / 10}`)

// ─── Line chart (two smooth curves, dashed marker, pill tooltips) ────

export interface LineSeries { key: string; label: string; values: number[]; tone: 'accent' | 'ink' }

export function LineChart({ labels, series, height = 240, formatValue = fmtDefault, defaultIndex, ariaLabel }: {
  labels: string[]
  series: LineSeries[]
  height?: number
  formatValue?: (v: number) => string
  /** Index the marker sits on before any hover (defaults to the last point). */
  defaultIndex?: number
  ariaLabel?: string
}) {
  const [ref, width] = useMeasure<HTMLDivElement>()
  const [hover, setHover] = useState<number | null>(null)
  const gid = useId().replace(/:/g, '')
  const padL = 34, padR = 12, padT = 52, padB = 28
  const n = labels.length
  const all = series.flatMap(s => s.values)
  const scale = niceScale(Math.max(1, ...all), 4)
  const iw = Math.max(10, width - padL - padR), ih = height - padT - padB
  const x = (i: number) => padL + (n <= 1 ? iw / 2 : (i / (n - 1)) * iw)
  const y = (v: number) => padT + ih - (v / scale.max) * ih
  const idx = hover ?? defaultIndex ?? Math.max(0, n - 1)
  const labelEvery = Math.max(1, Math.ceil(n / Math.max(2, Math.floor(iw / 64))))

  const onMove = (e: React.PointerEvent<SVGRectElement>) => {
    const r = (e.currentTarget as SVGRectElement).getBoundingClientRect()
    const px = e.clientX - r.left
    const i = n <= 1 ? 0 : Math.round((px / r.width) * (n - 1))
    setHover(Math.max(0, Math.min(n - 1, i)))
  }

  return (
    <div className="sf-chart" ref={ref} style={{ height }}>
      {width > 0 && (
        <svg width={width} height={height} role="img" aria-label={ariaLabel}>
          <defs>
            <linearGradient id={`lg-${gid}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--sf-accent)" stopOpacity="0.16" />
              <stop offset="100%" stopColor="var(--sf-accent)" stopOpacity="0" />
            </linearGradient>
          </defs>
          {scale.ticks.map(t => (
            <g key={t}>
              <line x1={padL} x2={width - padR} y1={y(t)} y2={y(t)} stroke="var(--sf-hair)" strokeWidth={1} />
              <text x={padL - 10} y={y(t) + 4} textAnchor="end">{formatValue(t)}</text>
            </g>
          ))}
          {labels.map((l, i) => (i % labelEvery === 0 || i === n - 1) && (
            <text key={i} x={x(i)} y={height - 6} textAnchor="middle" style={i === idx ? { fill: 'var(--sf-ink)', fontWeight: 600 } : undefined}>{l}</text>
          ))}
          {series.map(s => {
            const pts = s.values.map((v, i) => [x(i), y(v)] as [number, number])
            const d = smoothPath(pts)
            return (
              <g key={s.key}>
                {s.tone === 'accent' && pts.length > 1 && <path d={`${d} L${pts[pts.length - 1][0]},${y(0)} L${pts[0][0]},${y(0)} Z`} fill={`url(#lg-${gid})`} />}
                <path d={d} fill="none" stroke={s.tone === 'accent' ? 'var(--sf-accent)' : 'var(--sf-ink)'} strokeWidth={2.5} strokeLinecap="round" strokeDasharray={s.tone === 'ink' ? undefined : undefined} />
              </g>
            )
          })}
          {n > 0 && (
            <g>
              <line x1={x(idx)} x2={x(idx)} y1={padT - 6} y2={y(0)} stroke="var(--sf-ink)" strokeWidth={1.2} strokeDasharray="4 5" opacity={0.55} />
              {series.map(s => (
                <circle key={s.key} cx={x(idx)} cy={y(s.values[idx] ?? 0)} r={6} fill={s.tone === 'accent' ? 'var(--sf-accent)' : 'var(--sf-ink)'} stroke="var(--sf-card)" strokeWidth={3} />
              ))}
            </g>
          )}
          <rect x={padL} y={0} width={iw} height={height} fill="transparent" onPointerMove={onMove} onPointerLeave={() => setHover(null)} style={{ cursor: 'crosshair' }} />
        </svg>
      )}
      {width > 0 && n > 0 && (
        <div className="sf-tip" style={{ left: Math.min(Math.max(x(idx), 70), width - 70), top: padT - 10, display: 'flex', gap: 6, transform: 'translate(-50%, -100%)' }}>
          {series.map((s, si) => (
            <span key={s.key} className={`sf-tip-pill ${si === 0 ? '' : 'line'}`}>{formatValue(s.values[idx] ?? 0)} {s.label}</span>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Bar chart (rounded tops, highlighted bars, dashed average, bubble) ──

export function roundedTopBar(x: number, y: number, w: number, h: number, r: number): string {
  if (h <= 0) return ''
  const rr = Math.min(r, w / 2, h)
  return `M${x},${y + h} L${x},${y + rr} Q${x},${y} ${x + rr},${y} L${x + w - rr},${y} Q${x + w},${y} ${x + w},${y + rr} L${x + w},${y + h} Z`
}

export function BarChart({ labels, values, height = 220, highlight = 'above-average', formatValue = fmtDefault, valueLabel = '', showAverage = true, tooltipSub, ariaLabel }: {
  labels: string[]
  values: number[]
  height?: number
  /** Which bars get the solid brand gradient. */
  highlight?: 'above-average' | 'max' | 'last' | number[]
  formatValue?: (v: number) => string
  valueLabel?: string
  showAverage?: boolean
  tooltipSub?: (i: number) => string
  ariaLabel?: string
}) {
  const [ref, width] = useMeasure<HTMLDivElement>()
  const [hover, setHover] = useState<number | null>(null)
  const gid = useId().replace(/:/g, '')
  const padL = 30, padR = 6, padT = 56, padB = 28
  const n = values.length
  const scale = niceScale(Math.max(1, ...values), 3)
  const iw = Math.max(10, width - padL - padR), ih = height - padT - padB
  const slot = iw / Math.max(1, n)
  const bw = Math.max(6, Math.min(44, slot * 0.58))
  const y = (v: number) => padT + ih - (v / scale.max) * ih
  const avg = n ? values.reduce((a, b) => a + b, 0) / n : 0
  const maxIdx = values.reduce((mi, v, i) => (v > values[mi] ? i : mi), 0)
  const isHi = (i: number) => Array.isArray(highlight) ? highlight.includes(i)
    : highlight === 'max' ? i === maxIdx
    : highlight === 'last' ? i === n - 1
    : values[i] > 0 && values[i] >= avg
  const idx = hover ?? maxIdx
  const labelEvery = Math.max(1, Math.ceil(n / Math.max(2, Math.floor(iw / 40))))
  const cx = (i: number) => padL + slot * i + slot / 2

  return (
    <div className="sf-chart" ref={ref} style={{ height }}>
      {width > 0 && (
        <svg width={width} height={height} role="img" aria-label={ariaLabel}>
          <defs>
            <linearGradient id={`bg-${gid}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#fb923c" />
              <stop offset="100%" stopColor="#ea580c" />
            </linearGradient>
          </defs>
          {scale.ticks.map(t => (
            <g key={t}>
              <line x1={padL} x2={width - padR} y1={y(t)} y2={y(t)} stroke="var(--sf-hair)" />
              <text x={padL - 8} y={y(t) + 4} textAnchor="end">{formatValue(t)}</text>
            </g>
          ))}
          {values.map((v, i) => {
            const h = Math.max(v > 0 ? 4 : 0, (v / scale.max) * ih)
            return (
              <g key={i}>
                <rect x={padL + slot * i} y={padT} width={slot} height={ih} rx={14} fill={hover === i ? 'var(--sf-card-2)' : 'transparent'} />
                {v > 0
                  ? <path d={roundedTopBar(cx(i) - bw / 2, y(0) - h, bw, h, 10)} fill={isHi(i) ? `url(#bg-${gid})` : 'var(--sf-bar-pale)'} />
                  : <rect x={cx(i) - bw / 2} y={y(0) - 3} width={bw} height={3} rx={1.5} fill="var(--sf-hair-2)" />}
              </g>
            )
          })}
          {showAverage && n > 0 && avg > 0 && (
            <g>
              <line x1={padL} x2={width - padR} y1={y(avg)} y2={y(avg)} stroke="var(--sf-ink)" strokeDasharray="5 5" strokeWidth={1.2} opacity={0.6} />
              <rect x={padL + 2} y={y(avg) - 10} width={Math.max(56, (`avg ${formatValue(avg)}`).length * 6.6 + 12)} height={20} rx={10} fill="var(--sf-card)" stroke="var(--sf-hair-2)" />
              <text x={padL + 8} y={y(avg) + 4} textAnchor="start" style={{ fill: 'var(--sf-ink)', fontWeight: 600, fontSize: 11 }}>avg {formatValue(avg)}</text>
            </g>
          )}
          {labels.map((l, i) => (i % labelEvery === 0 || i === n - 1) && (
            <text key={i} x={cx(i)} y={height - 6} textAnchor="middle" style={i === idx ? { fill: 'var(--sf-ink)', fontWeight: 600 } : undefined}>{l}</text>
          ))}
          {values.map((_, i) => (
            <rect key={`h${i}`} x={padL + slot * i} y={0} width={slot} height={height} fill="transparent" onPointerEnter={() => setHover(i)} onPointerLeave={() => setHover(null)} />
          ))}
        </svg>
      )}
      {width > 0 && n > 0 && (
        <div className="sf-tip" style={{ left: Math.min(Math.max(cx(idx), 100), width - 100), top: y(values[idx] ?? 0) - 12 }}>
          <div className="sf-tip-bubble" style={{ whiteSpace: 'normal', width: 'max-content', maxWidth: 200 }}>
            <b>{formatValue(values[idx] ?? 0)}{valueLabel && <span style={{ fontWeight: 500, fontSize: 12 }}> {valueLabel}</span>}</b>
            <span>{tooltipSub ? tooltipSub(idx) : labels[idx]}</span>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Stacked bar chart ───────────────────────────────────────────────

export interface StackSeries { key: string; label: string; values: number[]; tone: 'accent' | 'ink' | 'pale' }
const stackFill = (t: StackSeries['tone']) => (t === 'accent' ? 'var(--sf-accent)' : t === 'ink' ? 'var(--sf-ink)' : '#fdba74')

export function StackedBarChart({ labels, series, height = 260, formatValue = fmtDefault, ariaLabel }: {
  labels: string[]
  series: StackSeries[]
  height?: number
  formatValue?: (v: number) => string
  ariaLabel?: string
}) {
  const [ref, width] = useMeasure<HTMLDivElement>()
  const [hover, setHover] = useState<number | null>(null)
  const padL = 44, padR = 6, padT = 16, padB = 28
  const n = labels.length
  const totals = labels.map((_, i) => series.reduce((a, s) => a + Math.max(0, s.values[i] || 0), 0))
  const scale = niceScale(Math.max(1, ...totals), 4)
  const iw = Math.max(10, width - padL - padR), ih = height - padT - padB
  const slot = iw / Math.max(1, n)
  const bw = Math.max(6, Math.min(34, slot * 0.56))
  const y = (v: number) => padT + ih - (v / scale.max) * ih
  const cx = (i: number) => padL + slot * i + slot / 2
  const labelEvery = Math.max(1, Math.ceil(n / Math.max(2, Math.floor(iw / 44))))

  return (
    <div className="sf-chart" ref={ref} style={{ height }}>
      {width > 0 && (
        <svg width={width} height={height} role="img" aria-label={ariaLabel}>
          {scale.ticks.map(t => (
            <g key={t}>
              <line x1={padL} x2={width - padR} y1={y(t)} y2={y(t)} stroke="var(--sf-hair)" />
              <text x={padL - 8} y={y(t) + 4} textAnchor="end">{formatValue(t)}</text>
            </g>
          ))}
          {labels.map((_, i) => {
            let acc = 0
            const segs = series.map(s => {
              const v = Math.max(0, s.values[i] || 0)
              const seg = { s, v, from: acc }
              acc += v
              return seg
            }).filter(sg => sg.v > 0)
            const topKey = segs[segs.length - 1]?.s.key
            return (
              <g key={i} opacity={hover === null || hover === i ? 1 : 0.45}>
                {segs.map(sg => {
                  const y0 = y(sg.from), y1 = y(sg.from + sg.v)
                  const h = Math.max(4, y0 - y1 - (sg.from > 0 ? 2 : 0))
                  return sg.s.key === topKey
                    ? <path key={sg.s.key} d={roundedTopBar(cx(i) - bw / 2, y0 - h - (sg.from > 0 ? 2 : 0), bw, h, 8)} fill={stackFill(sg.s.tone)} />
                    : <rect key={sg.s.key} x={cx(i) - bw / 2} y={y0 - h - (sg.from > 0 ? 2 : 0)} width={bw} height={h} fill={stackFill(sg.s.tone)} />
                })}
                {segs.length === 0 && <rect x={cx(i) - bw / 2} y={y(0) - 3} width={bw} height={3} rx={1.5} fill="var(--sf-hair-2)" />}
              </g>
            )
          })}
          {labels.map((l, i) => (i % labelEvery === 0 || i === n - 1) && (
            <text key={i} x={cx(i)} y={height - 6} textAnchor="middle" style={i === hover ? { fill: 'var(--sf-ink)', fontWeight: 600 } : undefined}>{l}</text>
          ))}
          {labels.map((_, i) => (
            <rect key={`h${i}`} x={padL + slot * i} y={0} width={slot} height={height} fill="transparent" onPointerEnter={() => setHover(i)} onPointerLeave={() => setHover(null)} />
          ))}
        </svg>
      )}
      {width > 0 && hover !== null && (
        <div className="sf-tip" style={{ left: Math.min(Math.max(cx(hover), 90), width - 90), top: y(totals[hover]) - 10 }}>
          <div className="sf-tip-bubble" style={{ minWidth: 150 }}>
            <span>{labels[hover]}</span>
            <b>{formatValue(totals[hover])}</b>
            {series.map(s => (
              <div key={s.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12 }}>
                <span><i style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 9, background: s.tone === 'ink' ? '#fff' : stackFill(s.tone), marginRight: 6 }} />{s.label}</span>
                <span style={{ color: 'inherit' }}>{formatValue(s.values[hover] || 0)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export function Legend({ items }: { items: Array<{ label: string; tone: 'accent' | 'ink' | 'pale' | 'dash' }> }) {
  return (
    <div className="sf-legend">
      {items.map(i => (
        <span key={i.label}>
          <i className={i.tone === 'dash' ? 'line-dash' : ''} style={i.tone === 'dash' ? undefined : { background: i.tone === 'accent' ? 'var(--sf-accent)' : i.tone === 'ink' ? 'var(--sf-ink)' : '#fdba74' }} />
          {i.label}
        </span>
      ))}
    </div>
  )
}

// ─── Trend (area / step line) ───────────────────────────────────────

export function TrendLine({ values, height = 96, step = true, labels, formatValue = fmtDefault }: { values: number[]; height?: number; step?: boolean; labels?: string[]; formatValue?: (v: number) => string }) {
  const [ref, width] = useMeasure<HTMLDivElement>()
  const [hover, setHover] = useState<number | null>(null)
  const gid = useId().replace(/:/g, '')
  const n = values.length
  const hi = Math.max(...values, 0), lo = Math.min(...values, hi)
  const span = Math.max(1, hi - lo)
  const max = hi + span * 0.25, min = Math.max(0, lo - span * 0.6)
  const pad = 6
  const x = (i: number) => pad + (n <= 1 ? 0 : (i / (n - 1)) * (width - pad * 2))
  const y = (v: number) => pad + (height - pad * 2) * (1 - (v - min) / (max - min || 1))
  let d = ''
  values.forEach((v, i) => {
    if (i === 0) d = `M${x(0)},${y(v)}`
    else if (step) d += ` H${(x(i - 1) + x(i)) / 2} V${y(v)} H${x(i)}`
    else d += ` L${x(i)},${y(v)}`
  })
  const last = hover ?? n - 1
  return (
    <div className="sf-chart" ref={ref} style={{ height }}>
      {width > 0 && n > 0 && (
        <svg width={width} height={height}
          onPointerMove={e => { const r = e.currentTarget.getBoundingClientRect(); setHover(Math.max(0, Math.min(n - 1, Math.round(((e.clientX - r.left) / r.width) * (n - 1))))) }}
          onPointerLeave={() => setHover(null)}>
          <defs>
            <linearGradient id={`tg-${gid}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--sf-accent)" stopOpacity="0.28" />
              <stop offset="100%" stopColor="var(--sf-accent)" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={`${d} V${height} H${x(0)} Z`} fill={`url(#tg-${gid})`} />
          <path d={d} fill="none" stroke="var(--sf-accent)" strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
          <circle cx={x(last)} cy={y(values[last])} r={5.5} fill="var(--sf-accent)" stroke="var(--sf-card)" strokeWidth={3} />
        </svg>
      )}
      {hover !== null && width > 0 && (
        <div className="sf-tip" style={{ left: Math.min(Math.max(x(hover), 50), width - 50), top: y(values[hover]) - 10 }}>
          <span className="sf-tip-pill">{formatValue(values[hover])}{labels?.[hover] ? ` · ${labels[hover]}` : ''}</span>
        </div>
      )}
    </div>
  )
}

// ─── Progress ring (timer style) ─────────────────────────────────────

export function ProgressRing({ value, max = 100, size = 180, stroke = 14, ticks = 60, children, tone = 'accent' }: { value: number; max?: number; size?: number; stroke?: number; ticks?: number; children?: React.ReactNode; tone?: 'accent' | 'ink' }) {
  const pct = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0
  const r = (size - stroke) / 2 - 10
  const c = 2 * Math.PI * r
  const [anim, setAnim] = useState(0)
  useEffect(() => { const t = requestAnimationFrame(() => setAnim(pct)); return () => cancelAnimationFrame(t) }, [pct])
  const tickEls = useMemo(() => Array.from({ length: ticks }, (_, i) => {
    const a = (i / ticks) * Math.PI * 2 - Math.PI / 2
    const r1 = size / 2 - 3, r2 = size / 2 - (i % 5 === 0 ? 9 : 6)
    return <line key={i} x1={size / 2 + Math.cos(a) * r1} y1={size / 2 + Math.sin(a) * r1} x2={size / 2 + Math.cos(a) * r2} y2={size / 2 + Math.sin(a) * r2} stroke={i / ticks <= pct ? 'var(--sf-accent)' : 'var(--sf-hair-2)'} strokeWidth={1.4} strokeLinecap="round" />
  }), [ticks, size, pct])
  return (
    <div className="sf-ring-wrap" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        {tickEls}
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--sf-pill)" strokeWidth={stroke} />
        {anim > 0 && <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={tone === 'ink' ? 'var(--sf-ink)' : 'var(--sf-accent)'} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={`${c * anim} ${c}`} transform={`rotate(-90 ${size / 2} ${size / 2})`} style={{ transition: 'stroke-dasharray 700ms ease' }} />}
      </svg>
      <div className="sf-ring-center">{children}</div>
    </div>
  )
}
